import type { FormResponseValue } from "@tellescope/types-models";
import type { FormFieldNode } from "../../utils/types";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";

export interface ResolveActiveResponsesArgs {
  currentResponses: LocalTellescopeFormResponseMap;
  fieldStack: FormFieldNode[] | undefined;
}

/**
 * The answers conditional logic may evaluate against: the ones the enduser actually navigated
 * through, not every response resolved into state.
 *
 * Seeded defaults and answers left on a branch the enduser backed out of are excluded, so an
 * abandoned branch cannot satisfy a later condition — matching legacy, which filtered on
 * `includeInSubmit`. A Question Group's sub-field answers ride along with the group, since scoring
 * reads them individually while only the group itself is navigated to.
 *
 * `fieldStack` is undefined until the enduser navigates, and absent entirely in the single-page
 * flow where every field is on screen at once. Both mean "nothing has been excluded", so every
 * response is active.
 */
export const resolveActiveResponses = ({
  currentResponses,
  fieldStack,
}: ResolveActiveResponsesArgs): FormResponseValue[] => {
  const allResponses = Object.values(currentResponses);
  if (!fieldStack?.length) return allResponses;

  const navigatedFieldIds = new Set(fieldStack.map((node) => node.value.id));
  const activeFieldIds = allResponses.reduce((acc, response) => {
    if (response.answer?.type !== "Question Group" || !navigatedFieldIds.has(response.fieldId)) return acc;
    (response.answer.value ?? []).forEach((subField) => {
      if (subField?.id) acc.add(subField.id);
    });
    return acc;
  }, navigatedFieldIds);

  return allResponses.filter((response) => activeFieldIds.has(response.fieldId));
};
